import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const MONO = "'Fira Code', 'Cascadia Code', 'Consolas', monospace";
const GREEN = "#22c55e";
const DARK_BG = "#060f0a";
const CARD_BG = "rgba(255,255,255,0.04)";
const CARD_BORDER = "1px solid rgba(255,255,255,0.08)";
const CODE_BG = "#0d1117";

// ── Syntax token colors ──────────────────────────────────────────────────────
const T = {
  method:  GREEN,
  key:     "#7ee787",
  str:     "#a5f3fc",
  num:     "#fbbf24",
  punct:   "#8b949e",
  comment: "#6e7681",
};

// ── Tiny SVG icons (inline, no external deps) ────────────────────────────────
function IconBolt() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function IconClock() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function IconWebhook() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11A2.99 2.99 0 0 0 18 8a3 3 0 1 0-3-3c0 .24.04.47.09.7L8.04 9.81A3 3 0 0 0 6 9a3 3 0 0 0 0 6 3 3 0 0 0 2.04-.81l7.13 4.16c-.05.21-.08.43-.08.65a3 3 0 1 0 3-3z" />
    </svg>
  );
}
function IconGrid() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconCheckSm() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ── Reusable CTA buttons ──────────────────────────────────────────────────────
function BtnPrimary({ children, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "#16a34a" : GREEN,
        color: "#052e16",
        border: "none",
        borderRadius: 8,
        padding: "11px 22px",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        transition: "background 0.18s",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </button>
  );
}
function BtnOutline({ children, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "rgba(255,255,255,0.07)" : "transparent",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.3)",
        borderRadius: 8,
        padding: "11px 22px",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        transition: "background 0.18s",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </button>
  );
}
function BtnOutlineDark({ children, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "rgba(255,255,255,0.07)" : "transparent",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.25)",
        borderRadius: 8,
        padding: "11px 22px",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        transition: "background 0.18s",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </button>
  );
}

// ── Terminal preview card ─────────────────────────────────────────────────────
function TerminalCard() {
  const panelStyle = {
    flex: 1,
    padding: "20px 22px",
    fontFamily: MONO,
    fontSize: 12.5,
    lineHeight: 1.75,
    overflowX: "auto",
    minWidth: 0,
  };

  return (
    <div style={{
      background: CODE_BG,
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.1)",
      boxShadow: "0 32px 64px rgba(0,0,0,0.5)",
      overflow: "hidden",
      marginTop: 56,
    }}>
      {/* Title bar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "12px 18px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.03)",
      }}>
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#ff5f57", display: "inline-block" }} />
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#ffbd2e", display: "inline-block" }} />
        <span style={{ width: 12, height: 12, borderRadius: "50%", background: "#28c840", display: "inline-block" }} />
        <span style={{ fontFamily: MONO, fontSize: 12, color: "#6b7280", marginLeft: 12 }}>
          <span style={{ color: T.method }}>POST</span>
          <span style={{ color: "#8b949e" }}> /v1/credits/issue</span>
        </span>
      </div>

      {/* Split view */}
      <div style={{ display: "flex" }}>
        {/* Request */}
        <div style={{ ...panelStyle, borderRight: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ color: T.comment, fontStyle: "italic", marginBottom: 10 }}>// Request</div>
          <div><span style={{ color: T.punct }}>{"{"}</span></div>
          <div style={{ paddingLeft: 16 }}>
            <JsonLine k="customer_id" v='"cus_9x2kl4m..."' vColor={T.str} />
            <JsonLine k="amount" v="2500" vColor={T.num} />
            <JsonLine k="currency" v='"usd"' vColor={T.str} />
            <JsonLine k="note" v='"VIP Loyalty Boost"' vColor={T.str} />
            <JsonLine k="expires_in_days" v="90" vColor={T.num} last />
          </div>
          <div><span style={{ color: T.punct }}>{"}"}</span></div>
        </div>

        {/* Response */}
        <div style={panelStyle}>
          <div style={{ color: T.comment, fontStyle: "italic", marginBottom: 10 }}>// Response 200 OK</div>
          <div><span style={{ color: T.punct }}>{"{"}</span></div>
          <div style={{ paddingLeft: 16 }}>
            <JsonLine k="credit_id" v='"crd_h7m3xp9..."' vColor={T.str} />
            <JsonLine k="status" v='"issued"' vColor={T.str} />
            <JsonLine k="balance" v="2500" vColor={T.num} />
            <JsonLine k="currency" v='"usd"' vColor={T.str} />
            <JsonLine k="issued_at" v='"2026-08-14T..."' vColor={T.str} />
            <JsonLine k="expires_at" v='"2026-11-12T..."' vColor={T.str} last />
          </div>
          <div><span style={{ color: T.punct }}>{"}"}</span></div>
        </div>
      </div>
    </div>
  );
}

function JsonLine({ k, v, vColor, last }) {
  return (
    <div>
      <span style={{ color: T.key }}>"{k}"</span>
      <span style={{ color: T.punct }}>: </span>
      <span style={{ color: vColor }}>{v}</span>
      {!last && <span style={{ color: T.punct }}>,</span>}
    </div>
  );
}

// ── Hero section ──────────────────────────────────────────────────────────────
function HeroSection({ navigate }) {
  const stats = [
    { val: "$47M+", label: "Credits issued via API" },
    { val: "99.99%", label: "Uptime SLA" },
    { val: "<50ms", label: "Avg response time" },
    { val: "5 SDKs", label: "Node · Python · Ruby · PHP · Go" },
  ];

  return (
    <section style={{
      background: `radial-gradient(ellipse at 80% 10%, rgba(34,197,94,0.10) 0%, transparent 55%), ${DARK_BG}`,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "80px 24px 60px",
      color: "#fff",
    }}>
      <div style={{ maxWidth: 1140, width: "100%", margin: "0 auto" }}>
        {/* Two-col top */}
        <div style={{
          display: "flex",
          gap: 48,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}>
          {/* Left col */}
          <div style={{ flex: "0 0 60%", minWidth: 280 }}>
            {/* Badge */}
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              border: `1px solid rgba(34,197,94,0.35)`,
              background: "rgba(34,197,94,0.06)",
              borderRadius: 20,
              padding: "4px 12px",
              marginBottom: 28,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: GREEN, display: "inline-block" }} />
              <span style={{ color: GREEN, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", fontFamily: MONO }}>
                STORE CREDIT API · v2.1
              </span>
            </div>

            {/* H1 */}
            <h1 style={{ margin: "0 0 20px", padding: 0, lineHeight: 1.1 }}>
              <span style={{ display: "block", fontSize: 64, fontWeight: 800, color: GREEN }}>Issue store credit.</span>
              <span style={{ display: "block", fontSize: 64, fontWeight: 800, color: "#fff" }}>Retain more customers.</span>
            </h1>

            {/* Subtext */}
            <p style={{
              fontSize: 15,
              color: "#9ca3af",
              maxWidth: 420,
              lineHeight: 1.7,
              margin: "0 0 32px",
            }}>
              Pabloo's REST API lets you issue, track, and redeem store credit programmatically — with SDKs, webhooks, and sub-50ms response times.
            </p>

            {/* CTAs */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <BtnPrimary onClick={() => window.location.href = "https://app.pabloo.com/sign-up"}>Get API keys →</BtnPrimary>
              <BtnOutline onClick={() => window.open("https://docs.pabloo.com", "_blank")}>Read the docs</BtnOutline>
            </div>
          </div>

          {/* Right col — stats grid */}
          <div style={{ flex: "0 0 calc(40% - 48px)", minWidth: 220 }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}>
              {stats.map((s, i) => (
                <div key={i} style={{
                  background: CARD_BG,
                  border: CARD_BORDER,
                  borderRadius: 12,
                  padding: 20,
                }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{s.val}</div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6, lineHeight: 1.4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Terminal preview */}
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <TerminalCard />
        </div>
      </div>
    </section>
  );
}

// ── Feature section 1 ─────────────────────────────────────────────────────────
const CODE_ISSUE = `import { Pabloo } from '@pabloo/node';

const pabloo = new Pabloo('sk_live_pbl_4f8d...');

// Issue store credit
const credit = await pabloo.credits.issue({
  customer_id: 'cus_9x2kl4m',
  amount: 2500,       // $25.00
  currency: 'usd',
  note: 'VIP Loyalty Boost',
  expires_in_days: 90,
});

console.log(credit.credit_id); // crd_h7m3xp9
console.log(credit.balance);   // 2500`;

function highlightNodeCode(raw) {
  // Very light tokenisation: keywords, strings, comments, numbers
  const lines = raw.split("\n");
  return lines.map((line, li) => {
    // Line comment
    if (line.trimStart().startsWith("//")) {
      return (
        <div key={li}>
          <span style={{ color: T.comment, fontStyle: "italic" }}>{line}</span>
        </div>
      );
    }
    // Inline comment at end
    const commentIdx = line.indexOf("//");
    let pre = line;
    let post = null;
    if (commentIdx > 0) {
      pre = line.slice(0, commentIdx);
      post = line.slice(commentIdx);
    }

    const tokenise = (src) => {
      // Tokenise imports / keywords / strings / numbers inline
      const parts = [];
      const re = /(import|from|const|await|new|return|true|false|null)(?=[\s(])|'[^']*'|"[^"]*"|`[^`]*`|\b\d+\b/g;
      let last = 0, m;
      while ((m = re.exec(src)) !== null) {
        if (m.index > last) parts.push(<span key={last} style={{ color: "#c9d1d9" }}>{src.slice(last, m.index)}</span>);
        const tok = m[0];
        if (/^(import|from|const|await|new|return|true|false|null)$/.test(tok)) {
          parts.push(<span key={m.index} style={{ color: "#ff7b72" }}>{tok}</span>);
        } else if (/^\d+$/.test(tok)) {
          parts.push(<span key={m.index} style={{ color: T.num }}>{tok}</span>);
        } else {
          parts.push(<span key={m.index} style={{ color: T.str }}>{tok}</span>);
        }
        last = m.index + tok.length;
      }
      if (last < src.length) parts.push(<span key={last} style={{ color: "#c9d1d9" }}>{src.slice(last)}</span>);
      return parts;
    };

    return (
      <div key={li}>
        {tokenise(pre)}
        {post && <span style={{ color: T.comment, fontStyle: "italic" }}>{post}</span>}
      </div>
    );
  });
}

const FEATURES = [
  { icon: <IconBolt />, title: "Issue & expire credits", desc: "POST /v1/credits/issue. Set amounts, expiry, and tags with a single API call." },
  { icon: <IconClock />, title: "Real-time balance checks", desc: "GET /v1/wallets/{id}. Sub-50ms globally consistent reads, every time." },
  { icon: <IconWebhook />, title: "Webhook events", desc: "Subscribe to credit.issued, credit.redeemed, credit.expired — fire-and-forget or retry-safe." },
  { icon: <IconGrid />, title: "Multi-store support", desc: "One API key, unlimited stores. Route credits across your entire brand portfolio." },
];

function FeatureSection1() {
  return (
    <section style={{ background: "#fff", padding: "96px 24px" }}>
      <div style={{ maxWidth: 1140, margin: "0 auto", display: "flex", gap: 64, flexWrap: "wrap", alignItems: "flex-start" }}>

        {/* Left */}
        <div style={{ flex: "0 0 calc(50% - 32px)", minWidth: 280 }}>
          <div style={{ color: GREEN, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>
            BUILT FOR DEVELOPERS
          </div>
          <h2 style={{ fontSize: 40, fontWeight: 800, color: "#111827", lineHeight: 1.15, margin: "0 0 36px" }}>
            Everything you need<br />to power store credit
          </h2>

          {FEATURES.map((f, i) => (
            <div key={i}>
              {i > 0 && <div style={{ height: 1, background: "#f3f4f6", margin: "18px 0" }} />}
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 7,
                  background: "rgba(34,197,94,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {f.icon}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{f.title}</div>
                  <div style={{ fontSize: 11.5, color: "#6b7280", lineHeight: 1.6, marginTop: 3 }}>{f.desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right — code editor */}
        <div style={{ flex: "0 0 calc(50% - 32px)", minWidth: 280 }}>
          <div style={{
            background: CODE_BG,
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.08)",
            overflow: "hidden",
          }}>
            {/* Tab bar */}
            <div style={{
              display: "flex",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.02)",
            }}>
              {["issue.js", "balance.js", "webhooks.js"].map((tab, i) => (
                <div key={i} style={{
                  padding: "10px 18px",
                  fontSize: 12,
                  fontFamily: MONO,
                  color: i === 0 ? "#c9d1d9" : "#6b7280",
                  borderBottom: i === 0 ? `2px solid ${GREEN}` : "2px solid transparent",
                  cursor: "default",
                  userSelect: "none",
                }}>
                  {tab}
                </div>
              ))}
            </div>
            {/* Code */}
            <div style={{
              padding: "22px 24px",
              fontFamily: MONO,
              fontSize: 12.5,
              lineHeight: 1.75,
              overflowX: "auto",
            }}>
              {highlightNodeCode(CODE_ISSUE)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Analytics widget ──────────────────────────────────────────────────────────
function AnalyticsWidget() {
  const events = [
    { label: "VIP Loyalty Boost", amount: "+$25.00", time: "2 min ago" },
    { label: "Cart Recovery", amount: "+$8.50", time: "5 min ago" },
    { label: "Welcome Bonus", amount: "+$15.00", time: "11 min ago" },
  ];

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      border: "1px solid #e5e7eb",
      boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
      padding: 24,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 20 }}>
        Credits overview · Last 30 days
      </div>

      {/* Stats */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Issued", val: "$47,340", change: "+12%", up: true },
          { label: "Redeemed", val: "$38,920", change: "+8%", up: true },
          { label: "Outstanding", val: "$8,420", change: null },
        ].map((r, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>{r.label}</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{r.val}</span>
              {r.change && (
                <span style={{ fontSize: 10, fontWeight: 600, color: "#22c55e", background: "#f0fdf4", borderRadius: 4, padding: "1px 5px" }}>
                  {r.change}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Sparkline */}
      <div style={{ borderRadius: 8, overflow: "hidden", marginBottom: 20, background: "#f9fafb" }}>
        <svg viewBox="0 0 300 80" width="100%" height="80" preserveAspectRatio="none">
          <defs>
            <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0 65 C20 62, 35 55, 55 50 C75 45, 85 52, 105 44 C125 36, 140 30, 160 26 C180 22, 195 28, 215 20 C235 12, 250 18, 270 12 C285 7, 295 9, 300 6"
            fill="none"
            stroke="#22c55e"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          <path
            d="M0 65 C20 62, 35 55, 55 50 C75 45, 85 52, 105 44 C125 36, 140 30, 160 26 C180 22, 195 28, 215 20 C235 12, 250 18, 270 12 C285 7, 295 9, 300 6 L300 80 L0 80 Z"
            fill="url(#sg)"
          />
        </svg>
      </div>

      {/* Recent events */}
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Recent events
      </div>
      {events.map((e, i) => (
        <div key={i} style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 0",
          borderTop: i === 0 ? "none" : "1px solid #f3f4f6",
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#111827" }}>{e.label}</div>
            <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 1 }}>{e.time}</div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: GREEN }}>{e.amount}</div>
        </div>
      ))}
    </div>
  );
}

// ── Feature section 2 ─────────────────────────────────────────────────────────
const RT_BULLETS = [
  {
    icon: <IconWebhook />,
    title: "Realtime event stream",
    desc: "Every credit.issued and credit.redeemed fires instantly to your webhook endpoint.",
  },
  {
    icon: <IconClock />,
    title: "Dashboard analytics",
    desc: "Track issuance, redemption rates, and outstanding balances from your Pabloo dashboard.",
  },
  {
    icon: <IconGrid />,
    title: "Exportable reports",
    desc: "Pull full transaction history via API or export CSV from the dashboard — finance-ready.",
  },
];

function FeatureSection2() {
  return (
    <section style={{ background: "#f9fafb", padding: "96px 24px" }}>
      <div style={{ maxWidth: 1140, margin: "0 auto", display: "flex", gap: 64, flexWrap: "wrap", alignItems: "center" }}>

        {/* Left — widget */}
        <div style={{ flex: "0 0 calc(50% - 32px)", minWidth: 280 }}>
          <AnalyticsWidget />
        </div>

        {/* Right — text */}
        <div style={{ flex: "0 0 calc(50% - 32px)", minWidth: 280 }}>
          <div style={{ color: GREEN, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>
            REAL-TIME ANALYTICS
          </div>
          <h2 style={{ fontSize: 40, fontWeight: 800, color: "#111827", lineHeight: 1.15, margin: "0 0 36px" }}>
            Monitor every credit<br />in real time
          </h2>

          {RT_BULLETS.map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 24 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: GREEN,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, marginTop: 1,
              }}>
                <IconCheckSm />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{b.title}</div>
                <div style={{ fontSize: 11.5, color: "#6b7280", lineHeight: 1.6, marginTop: 3 }}>{b.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Teams section ─────────────────────────────────────────────────────────────
const TEAM_COLORS = ["#22c55e", "#16a34a", "#bbf7d0", "#4ade80"];
const TEAMS = [
  {
    title: "Developers",
    desc: "Ship store credit in an afternoon. SDKs, webhooks, idempotent API, OpenAPI spec. No surprises.",
  },
  {
    title: "Growth teams",
    desc: "Design cashback campaigns that drive repeat purchases — configure rules without writing a line of code.",
  },
  {
    title: "Commerce Ops",
    desc: "Track every credit across all your stores. Full audit trail, exportable reports, zero reconciliation headaches.",
  },
  {
    title: "Finance",
    desc: "Real-time credit liability reporting. Know exactly what's outstanding, what's expired, and what's been redeemed.",
  },
];

function FourSquaresIcon({ colors }) {
  const [a, b, c, d] = colors;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, width: 32, height: 32 }}>
      {[a, b, c, d].map((col, i) => (
        <div key={i} style={{ borderRadius: 3, background: col, width: "100%", height: "100%" }} />
      ))}
    </div>
  );
}

function TeamsSection() {
  return (
    <section style={{ background: "#fff", padding: "80px 24px" }}>
      <div style={{ maxWidth: 1140, margin: "0 auto" }}>
        <h2 style={{ fontSize: 40, fontWeight: 800, color: "#111827", lineHeight: 1.15, textAlign: "center", margin: "0 0 56px" }}>
          Built for every<br />commerce team
        </h2>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 20,
        }}>
          {TEAMS.map((t, i) => (
            <div key={i} style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
            }}>
              <FourSquaresIcon colors={TEAM_COLORS} />
              <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginTop: 16 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.7, marginTop: 6 }}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA section ───────────────────────────────────────────────────────────────
function CtaSection({ navigate }) {
  const trust = ["No credit card required", "Free 14-day trial", "Shopify certified"];
  return (
    <section style={{
      background: `radial-gradient(ellipse at 50% 0%, rgba(34,197,94,0.15) 0%, transparent 70%), ${DARK_BG}`,
      padding: "100px 24px",
      textAlign: "center",
      color: "#fff",
    }}>
      <h2 style={{ fontSize: 48, fontWeight: 800, lineHeight: 1.1, margin: "0 0 20px" }}>
        Start issuing store credit<br />today.
      </h2>
      <p style={{ fontSize: 15, color: "#9ca3af", maxWidth: 400, margin: "0 auto 36px", lineHeight: 1.7 }}>
        Get your API keys in minutes. Connect your store, set your cashback rules, and go live.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 32 }}>
        <BtnPrimary onClick={() => window.location.href = "https://app.pabloo.com/sign-up"}>Get API keys →</BtnPrimary>
        <BtnOutlineDark onClick={() => navigate("/bookCall")}>Talk to sales</BtnOutlineDark>
      </div>
      <div style={{ display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap" }}>
        {trust.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{
              width: 18, height: 18, borderRadius: "50%",
              background: GREEN,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <IconCheck />
            </div>
            <span style={{ fontSize: 12.5, color: "#9ca3af" }}>{s}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function APIPageHomeComponent() {
  const navigate = useNavigate();

  return (
    <div>
      <HeroSection navigate={navigate} />
      <FeatureSection1 />
      <FeatureSection2 />
      <TeamsSection />
      <CtaSection navigate={navigate} />
    </div>
  );
}
